import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import type { OrderState, RiskLevel } from './api';

/* ── Money ────────────────────────────────────────────────────────────────
   Amounts arrive from the server pre-formatted. The client never does
   arithmetic on them, so there is no path for float drift to reappear. */

export function formatMinor(minor: number, currency = 'INR'): string {
  const neg = minor < 0;
  const whole = Math.floor(Math.abs(minor) / 100);
  const paise = Math.abs(minor) % 100;
  let s = String(whole);
  if (s.length > 3) {
    const tail = s.slice(-3);
    let head = s.slice(0, -3);
    const parts: string[] = [];
    while (head.length > 2) {
      parts.unshift(head.slice(-2));
      head = head.slice(0, -2);
    }
    if (head) parts.unshift(head);
    s = [...parts, tail].join(',');
  }
  const sym = currency === 'INR' ? '\u20b9' : `${currency} `;
  return `${neg ? '-' : ''}${sym}${s}.${String(paise).padStart(2, '0')}`;
}

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export const titleise = (s: string) =>
  s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/* ── Pills ─────────────────────────────────────────────────────────────── */

const STATE_STYLE: Record<OrderState, string> = {
  DRAFT: 'pill-draft',
  IN_APPROVAL: 'pill-wait',
  APPROVED: 'pill-ok',
  REJECTED: 'pill-bad',
  PUBLISHED: 'pill-live',
  EXECUTING: 'pill-info',
  SETTLED: 'pill-ok',
  CLOSED: 'pill-draft',
  CANCELLED: 'pill-draft',
};

export function StatePill({ state }: { state: OrderState }) {
  return (
    <span className={`pill ${STATE_STYLE[state]}`}>
      <span className="dot" aria-hidden="true" />
      {titleise(state)}
    </span>
  );
}

const RISK_STYLE: Record<RiskLevel, string> = {
  CRITICAL: 'pill-bad',
  HIGH: 'pill-bad',
  MEDIUM: 'pill-wait',
  LOW: 'pill-info',
  INFO: 'pill-draft',
};

export function RiskPill({ level, count }: { level: RiskLevel; count?: number }) {
  if (level === 'INFO' && !count) return null;
  return (
    <span className={`pill ${RISK_STYLE[level]}`}>
      {level === 'CRITICAL' || level === 'HIGH' ? '\u26a0' : ''} {titleise(level)}
      {count ? ` · ${count}` : ''}
    </span>
  );
}

/* ── The Seal ──────────────────────────────────────────────────────────────
   The signature element: a payload digest presented as a security band. When
   an edit invalidates approvals, the band visibly breaks. */

export function Seal({
  digest,
  broken = false,
  label,
}: {
  digest: string;
  broken?: boolean;
  label?: string;
}) {
  const head = digest.slice(0, 8);
  const rest = digest.slice(8);
  return (
    <div className={`seal${broken ? ' broken' : ''}`}>
      <div className="seal-label">
        {broken ? (
          <>
            <BreakIcon /> Seal broken — approvals invalidated
          </>
        ) : (
          <>
            <SealIcon /> {label ?? 'Approval seal'}
          </>
        )}
      </div>
      <div className="seal-digest" title={digest}>
        <b>{head}</b>
        {rest}
      </div>
    </div>
  );
}

/* ── Toasts ────────────────────────────────────────────────────────────── */

type Toast = { id: number; message: string; tone: 'ok' | 'bad' | 'info' };
type ToastApi = (message: string, tone?: Toast['tone']) => void;

const ToastCtx = createContext<ToastApi>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);

  const push = useCallback<ToastApi>((message, tone = 'info') => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, message, tone }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 4200);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>{t.message}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ── Modal ─────────────────────────────────────────────────────────────── */

export function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="overlay" onClick={onClose} role="presentation">
      <div
        className={`modal${wide ? ' modal-lg' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="row between" style={{ marginBottom: 18 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{title}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <XIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── States ────────────────────────────────────────────────────────────── */

export function Skeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="col gap-10" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height: 44, opacity: 1 - i * 0.13 }} />
      ))}
    </div>
  );
}

export function Empty({ title, hint, action }: { title: string; hint: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      <div style={{ maxWidth: 380, margin: '0 auto 16px' }}>{hint}</div>
      {action}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.';
  return <div className="alert alert-bad">{message}</div>;
}

/* ── Icons ─────────────────────────────────────────────────────────────── */

const svg = (d: ReactNode, size = 17) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    {d}
  </svg>
);

export const DashIcon = () => svg(<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>);
export const OrdersIcon = () => svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>);
export const ShieldIcon = () => svg(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />);
export const AuditIcon = () => svg(<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>);
export const AgentIcon = () => svg(<><path d="M12 2a3 3 0 0 1 3 3v1h2a3 3 0 0 1 3 3v2" /><rect x="4" y="9" width="16" height="11" rx="2" /><circle cx="9" cy="14" r="1" /><circle cx="15" cy="14" r="1" /></>);
export const MenuIcon = () => svg(<><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>);
export const XIcon = () => svg(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>, 15);
export const PlusIcon = () => svg(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>, 15);
export const SealIcon = () => svg(<><circle cx="12" cy="9" r="6" /><path d="M9 14.5 8 22l4-2 4 2-1-7.5" /></>, 13);
export const BreakIcon = () => svg(<><path d="M12 2v6l-3 2 3 4-2 8" /><circle cx="12" cy="9" r="6" opacity=".4" /></>, 13);
export const BackIcon = () => svg(<><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>, 15);
export const CheckIcon = () => svg(<polyline points="20 6 9 17 4 12" />, 15);
export const UserIcon = () => svg(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>, 15);
export const AdminIcon = () => svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>);
export const DocIcon = () => svg(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>, 15);
export const PayrollIcon = () => svg(<><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><line x1="6" y1="14" x2="10" y2="14" /><line x1="14" y1="14" x2="16" y2="14" /></>);
export const BeneficiaryIcon = () => svg(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>);

/* ── Hooks ─────────────────────────────────────────────────────────────── */

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function useKeyboardShortcut(key: string, handler: () => void, meta = true) {
  const cb = useMemo(() => handler, [handler]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const modifier = meta ? e.metaKey || e.ctrlKey : true;
      if (modifier && e.key.toLowerCase() === key.toLowerCase()) {
        e.preventDefault();
        cb();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [key, cb, meta]);
}
